<template>
    <glass-panel
        radius="lg"
        class="analytics-geo pa-6"
    >
        <div class="d-flex align-center justify-space-between mb-5" style="gap:12px">
            <div>
                <eyebrow primary class="mb-1">
                    Audiência por região
                </eyebrow>
                <h3 class="analytics-geo__title">
                    De onde vêm seus visitantes
                </h3>
            </div>
            <v-icon color="primary" size="22">mdi-map-marker-radius-outline</v-icon>
        </div>

        <div
            v-if="!ranked.length"
            class="analytics-geo__empty"
        >
            <v-icon size="32" color="primary">mdi-earth</v-icon>
            <p class="mt-3 mb-0 body-sm">Sem dados geográficos disponíveis ainda.</p>
        </div>

        <ul
            v-else
            class="analytics-geo__list"
        >
            <li
                v-for="(row, idx) in topRows"
                :key="row.label"
                class="analytics-geo__row"
            >
                <span class="analytics-geo__rank">{{ idx + 1 }}</span>
                <span class="analytics-geo__flag" :title="row.label">{{ row.flag }}</span>
                <div class="analytics-geo__info">
                    <div class="analytics-geo__name">{{ row.label }}</div>
                    <div class="analytics-geo__bar">
                        <span
                            class="analytics-geo__bar-fill"
                            :style="{ width: `${row.pct}%` }"
                        />
                    </div>
                </div>
                <div class="analytics-geo__metric">
                    <div class="analytics-geo__visitors">{{ formatNumber(row.visitors) }}</div>
                    <div class="analytics-geo__pct">{{ row.share }}%</div>
                </div>
            </li>
        </ul>

        <div
            v-if="extraCount > 0"
            class="analytics-geo__more"
        >
            +{{ extraCount }} {{ extraCount === 1 ? 'localidade' : 'localidades' }}
        </div>
    </glass-panel>
</template>

<script>
const COUNTRY_NAMES = {
    BR: 'Brasil',
    US: 'Estados Unidos',
    PT: 'Portugal',
    AR: 'Argentina',
    CL: 'Chile',
    UY: 'Uruguai',
    PY: 'Paraguai',
    CO: 'Colômbia',
    MX: 'México',
    PE: 'Peru',
    EC: 'Equador',
    VE: 'Venezuela',
    BO: 'Bolívia',
    CA: 'Canadá',
    GB: 'Reino Unido',
    ES: 'Espanha',
    IT: 'Itália',
    FR: 'França',
    DE: 'Alemanha',
    JP: 'Japão',
    CN: 'China',
    IN: 'Índia',
    AU: 'Austrália',
}

const TOP_LIMIT = 8

const formatNumber = (n) => {
    if (n == null || isNaN(n)) return '—'
    return Number(n).toLocaleString('pt-BR')
}

const countryCodeToFlag = (code) => {
    if (!code || code.length !== 2) return '🌐'
    const cp = [...code.toUpperCase()].map(c => 0x1f1e6 - 65 + c.charCodeAt(0))
    if (cp.some(p => p < 0x1f1e6 || p > 0x1f1ff)) return '🌐'
    return String.fromCodePoint(...cp)
}

const resolveCountryName = (raw) => {
    if (!raw) return 'Desconhecido'
    const upper = String(raw).toUpperCase()
    if (COUNTRY_NAMES[upper]) return COUNTRY_NAMES[upper]
    return raw
}

export default {
    name: 'AnalyticsGeoList',
    props: {
        geo: {
            type: Array,
            default: () => [],
        },
    },
    methods: { formatNumber },
    computed: {
        ranked() {
            if (!Array.isArray(this.geo)) return []
            const buckets = new Map()
            for (const item of this.geo) {
                const country = item?.dimension?.country
                if (!country) continue
                const key = country
                const visitors = Number(item.data?.VISITORS) || 0
                if (!buckets.has(key)) {
                    buckets.set(key, {
                        country,
                        visitors: 0,
                    })
                }
                buckets.get(key).visitors += visitors
            }
            const rows = Array.from(buckets.values())
                .filter(r => r.visitors > 0)
                .sort((a, b) => b.visitors - a.visitors)

            const total = rows.reduce((sum, r) => sum + r.visitors, 0)
            const max = rows[0]?.visitors || 1
            return rows.map(r => {
                const isCode = typeof r.country === 'string' && r.country.length === 2
                return {
                    label: resolveCountryName(r.country),
                    flag: isCode ? countryCodeToFlag(r.country) : '🌐',
                    visitors: r.visitors,
                    pct: Math.max(4, Math.round((r.visitors / max) * 100)),
                    share: total > 0 ? Math.round((r.visitors / total) * 100) : 0,
                }
            })
        },
        topRows() {
            return this.ranked.slice(0, TOP_LIMIT)
        },
        extraCount() {
            return Math.max(0, this.ranked.length - TOP_LIMIT)
        },
    },
}
</script>

<style scoped>
.analytics-geo__title {
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 1.25rem;
    letter-spacing: -0.01em;
    color: var(--color-dark-violet);
    margin: 0;
}

html.theme-dark .analytics-geo__title {
    color: var(--color-on-surface);
}

.analytics-geo__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.analytics-geo__row {
    display: grid;
    grid-template-columns: 24px 28px 1fr auto;
    align-items: center;
    gap: 12px;
}

.analytics-geo__rank {
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 0.75rem;
    color: var(--color-on-surface-variant);
    text-align: center;
}

.analytics-geo__flag {
    font-size: 1.375rem;
    line-height: 1;
    text-align: center;
}

.analytics-geo__info {
    min-width: 0;
}

.analytics-geo__name {
    font-family: var(--font-body);
    font-weight: 600;
    font-size: 0.875rem;
    color: var(--color-on-surface);
    margin-bottom: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.analytics-geo__bar {
    height: 6px;
    border-radius: 9999px;
    background: rgba(112, 42, 225, 0.08);
    overflow: hidden;
}

.analytics-geo__bar-fill {
    display: block;
    height: 100%;
    background: linear-gradient(90deg, var(--color-primary), var(--color-primary-fixed));
    border-radius: 9999px;
    transition: width 600ms ease;
}

.analytics-geo__metric {
    text-align: right;
    min-width: 70px;
}

.analytics-geo__visitors {
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 0.9375rem;
    color: var(--color-dark-violet);
    line-height: 1;
}

html.theme-dark .analytics-geo__visitors {
    color: var(--color-on-surface);
}

.analytics-geo__pct {
    font-family: var(--font-body);
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--color-on-surface-variant);
    margin-top: 4px;
}

.analytics-geo__more {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px dashed rgba(112, 42, 225, 0.15);
    text-align: center;
    font-family: var(--font-body);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--color-on-surface-variant);
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.analytics-geo__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 32px 16px;
    color: var(--color-on-surface-variant);
}
</style>
